import React, { useState } from 'react';

import { L } from '../../../../lib/i18n';
import styled from '@emotion/styled';
import CardWithDivider from '../../../../components/Layout/CardWithDivider';

import CardTitle from '../../../../components/typography/CardTitle';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { useHistory } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import BoardContentInformationApi from '@app/api/public/BoardContentInformationApi';
import toast from 'react-hot-toast';
import Label from '../../../../components/Forms/Label';
import ImageDropZone from '../../../../components/ImageDropZone/ImageDropZone';
import Input from '../../../../components/Forms/Input';
import { style } from '../../../../components/Shared/Style';
import { Button } from '../../../../components/Buttons/Button';
import triggerDirtyFieldsToats from '../../../../components/Forms/DirtyFieldsToast';
import Quill from '../../../../components/Quill/Quill';
import { boardInformationQueryKeys } from '../hooks/boardInformationQueries';
import DropdownButton from '../../../../components/Buttons/DropdownButton';
import { useLocalStorage } from 'react-use';
import { notificationToast } from '../../notificationToast/notificationToast';

const StyledCardWithDivider = styled(CardWithDivider)({
    width: 'fit-content',
});

const GridContainer = styled.div({
    display: 'grid',
    gap: '1rem',
    columnGap: '2rem',
    backgroundColor: style.colors.white,
    alignItems: 'start',
    gridTemplateColumns: '1fr 1fr 1fr',
    gridTemplateAreas: `
    "img          img               img"
    "title        title             title"
    "description  description description"
    "buttons      buttons         buttons"
    `,
    paddingBottom: '0px',
    maxWidth: '600px',
});

const StyledImageDropZone = styled(ImageDropZone)({
    aspectRatio: '4/3',
});

const informationFormSchema = z.object({
    title: z.string().min(1, { message: L('required') }),
    description: z.string().min(1, { message: L('required') }),
});

type CreateInformation = { information: z.infer<typeof informationFormSchema>; image?: File };

const createInformationDraft = (variables: CreateInformation) => {
    return BoardContentInformationApi.createDraft(variables);
};

type CreateInformationAndPublish = {
    information: z.infer<typeof informationFormSchema>;
    image?: File;
    notify: boolean;
};
const createNewInformation = (variables: CreateInformationAndPublish) => {
    return BoardContentInformationApi.createInformationAndPublish(variables);
};

const CreateNewInformationPage = () => {
    const history = useHistory();
    const [localSubmitButtonState, setSubmitButtonState] = useLocalStorage<'save_to_draft' | 'save_and_publish'>(
        'boardCreateNewInformation',
        'save_and_publish'
    );
    const submitButtonState = localSubmitButtonState || 'save_and_publish';
    const quertyClient = useQueryClient();
    const { mutate: createDraft } = useMutation((variables: CreateInformation) => createInformationDraft(variables), {
        onSettled: () => {
            quertyClient.invalidateQueries(boardInformationQueryKeys.lists());
        },
    });
    const { mutate: createAndPublishInformation } = useMutation(
        (variables: CreateInformationAndPublish) => createNewInformation(variables),
        {
            onMutate: () => {
                toast.loading(L('loading'), {
                    id: 'send-notification',
                });
            },
            onSettled: () => {
                quertyClient.invalidateQueries(boardInformationQueryKeys.lists());
            },
        }
    );

    const [imgFile, setImgFile] = useState<File>();
    const [imgUrl, setImgUrl] = useState<string>('');

    const onUpload = (files: File[]) => {
        setImgFile(files[0]);
        setImgUrl(URL.createObjectURL(files[0]));
    };

    const {
        register,
        handleSubmit,
        control,
        formState: { errors, isDirty },
    } = useForm({
        resolver: zodResolver(informationFormSchema),
        defaultValues: {
            title: '',
            description: '',
        },
    });

    const submit = (data: z.infer<typeof informationFormSchema>, type: 'publish' | 'draft') => {
        if (type === 'publish') {
            const publish = (notify: boolean) => {
                return createAndPublishInformation(
                    {
                        information: {
                            ...data,
                        },
                        image: imgFile,
                        notify,
                    },
                    {
                        onSuccess: (data) => {
                            toast.success(L('information_created_and_published'));
                            if (data?.id) {
                                history.push(`/board-information/${data.id}`);
                            }
                        },
                        onSettled: () => {
                            toast.dismiss('send-notification');
                        },
                    }
                );
            };

            return notificationToast({
                notify: (notify) => publish(notify),
                type: 'publish',
            });
        }

        createDraft(
            {
                information: {
                    ...data,
                },
                image: imgFile,
            },
            {
                onSuccess: (data) => {
                    toast.success(L('information_saved_as_draft'));
                    if (data?.id) {
                        history.push(`/board-information/${data.id}`);
                    }
                },
            }
        );
    };

    return (
        <StyledCardWithDivider
            topArea={<CardTitle>{L('information_new')}</CardTitle>}
            mainArea={
                <GridContainer>
                    <div style={{ gridArea: 'img' }}>
                        <Label title={L('image')} />
                        <StyledImageDropZone
                            dropzoneOptions={{}}
                            onDrop={onUpload}
                            url={imgUrl || ''}
                            remove={() => {
                                if (imgUrl || imgFile) {
                                    setImgUrl('');
                                    setImgFile(undefined);
                                }
                            }}
                        />
                    </div>

                    <Label
                        style={{ gridArea: 'title' }}
                        htmlFor="title"
                        title={`${L('title')}*`}
                        error={errors?.title?.message}
                    >
                        <Input {...register('title', { required: true })} />
                    </Label>

                    <div style={{ gridArea: 'description' }}>
                        <Label title={`${L('description')}*`} error={errors?.description?.message} />
                        <Controller
                            name="description"
                            control={control}
                            render={({ field }) => {
                                return <Quill {...field} placeholder="Skriv något här..." />;
                            }}
                        />
                    </div>
                    <div
                        style={{
                            placeSelf: 'end',
                            whiteSpace: 'nowrap',
                            display: 'flex',
                            gap: style.margin.m,
                            gridArea: 'buttons',
                        }}
                    >
                        <Button
                            type="button"
                            variant="primary"
                            onClick={() => {
                                if (isDirty) {
                                    return triggerDirtyFieldsToats({
                                        continueButtonText: L('continue_without_saving'),
                                        continueFn: () => {
                                            history.push('/board-information');
                                        },
                                    });
                                }
                                history.push('/board-information');
                            }}
                            role="link"
                        >
                            {L('cancel')}
                        </Button>

                        <DropdownButton
                            optionId={submitButtonState}
                            variant={'filledPrimary'}
                            setOptionId={(value) => setSubmitButtonState(value)}
                            options={[
                                {
                                    id: 'save_to_draft',
                                    dropdownItem: (
                                        <DropdownButton.DropdownItem>{L('save_to_draft')}</DropdownButton.DropdownItem>
                                    ),
                                    button: (
                                        <DropdownButton.Button
                                            onClick={handleSubmit((formValues) => submit(formValues, 'draft'))}
                                        >
                                            {L('save_to_draft')}
                                        </DropdownButton.Button>
                                    ),
                                },
                                {
                                    id: 'save_and_publish',
                                    dropdownItem: (
                                        <DropdownButton.DropdownItem>
                                            {L('save_and_publish')}
                                        </DropdownButton.DropdownItem>
                                    ),
                                    button: (
                                        <DropdownButton.Button
                                            onClick={handleSubmit((formValues) => submit(formValues, 'publish'))}
                                        >
                                            {L('save_and_publish')}
                                        </DropdownButton.Button>
                                    ),
                                },
                            ]}
                        />
                    </div>
                </GridContainer>
            }
        />
    );
};

export default CreateNewInformationPage;
